/**
 * Staff data access for submissions, via the browser Supabase client (RLS-guarded).
 * Public agent flows do NOT use these — they go through the backend api client.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GatekeeperCarrier, SubmissionData } from '@commpass/shared';
import { api } from '../lib/api';
import { supabase } from '../lib/supabase';
import type { SubmissionRow } from '../types';

export function useSubmissions() {
  return useQuery<SubmissionRow[]>({
    queryKey: ['submissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('submissions')
        .select('id, created_at, updated_at, status, tier, business_name, submitter_email, data')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SubmissionRow[];
    },
  });
}

export function useSubmission(id: string | undefined) {
  return useQuery<SubmissionRow | null>({
    queryKey: ['submission', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('submissions')
        .select('id, created_at, updated_at, status, tier, business_name, submitter_email, data')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return (data as SubmissionRow) ?? null;
    },
  });
}

/**
 * Gatekeeper underwriter contacts — fetched from the staff-only backend endpoint
 * (never in the browser bundle). Enable only when actually needed (a Gatekeeper
 * submission). Cached long since it's static reference data.
 */
export function useGatekeeperCarriers(enabled: boolean) {
  return useQuery<GatekeeperCarrier[]>({
    queryKey: ['gatekeeper-carriers'],
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => (await api.getGatekeeperCarriers()).carriers,
  });
}

/** Patch a submission's status + data blob (staff). */
export function useUpdateSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status?: string; data: SubmissionData }) => {
      // The whole `data` jsonb column is replaced on update, so writing a snapshot that
      // loaded before someone else added an internal note or message would silently wipe
      // it. Those fields are only ever written by their own server endpoints, never here,
      // so re-read the freshest copy and keep them intact regardless of what this caller
      // happened to load.
      let data: SubmissionData = input.data;
      const { data: fresh } = await supabase
        .from('submissions')
        .select('data')
        .eq('id', input.id)
        .maybeSingle();
      const fd = fresh?.data as SubmissionData | undefined;
      if (fd) {
        data = {
          ...input.data,
          internalNotes: fd.internalNotes ?? input.data.internalNotes,
          messages: fd.messages ?? input.data.messages,
        };
      }
      const patch: Record<string, unknown> = { data };
      if (input.status) patch.status = input.status;
      if (data.businessName != null) patch.business_name = data.businessName;
      if (data.submitterEmail != null) patch.submitter_email = data.submitterEmail;
      if (data.tier != null) patch.tier = data.tier;
      const { error } = await supabase.from('submissions').update(patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_res, input) => {
      void qc.invalidateQueries({ queryKey: ['submission', input.id] });
      void qc.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
}
